/**
 * BillingGatedRoute — the route-level dark guard for the two billing PAGES
 * (PremiumHub at /prep/premium, ActivationPage at /prep/premium/activating).
 *
 * WHY A SEPARATE WRAPPER FROM THE "render null" COMPONENT PIN: a widget
 * (UpgradeDialog, GuardianPremiumEntry, EntitlementStatusBlock) can safely
 * render null while billing is off or still loading — it's one piece of a
 * bigger page. A full ROUTE cannot: rendering null there is a blank page,
 * and redirecting the INSTANT `enabled !== true` (which is also true during
 * the brief 'loading' window) would bounce a genuinely-enabled user away
 * before the entitlement check even resolves — a real bug for the
 * activation page specifically, which is landed on right after a real
 * checkout. This wrapper WAITS for `status==='loading'` to resolve before
 * deciding, so it never redirects a user for whom billing is actually on.
 *
 * "404-redirects while dark" (the task's phrasing): mirrors the house
 * pattern for a hidden/unreachable route (Issue #56's hidden_sections
 * guard, App.js) — a plain client-side `<Navigate replace>` to a real,
 * always-safe page, not a literal rendered "404" (this SPA has no dedicated
 * NotFound component; every other unreachable-route case in this app
 * degrades the same way).
 *
 * `status === 'error'` (F14-PR3.2, review S4) is DELIBERATELY NOT folded
 * into the 'disabled' redirect: it means a TRANSIENT failure (network/5xx/
 * timeout), not a confirmed dark signal — a student landing here right
 * after a real Stripe checkout is the WORST possible moment to bounce them
 * away on a single blip that has nothing to do with whether billing is
 * actually on. This state offers an in-place retry (re-running the SAME
 * `useBilling().refresh()` every other retry affordance in this module
 * uses) instead, and only ever redirects once 'disabled' is genuinely
 * CONFIRMED.
 */
import { useState } from 'react';
import { Navigate } from 'react-router';
import { Button, Spinner } from '../components/ui';
import { useBilling } from './BillingContext';
import './BillingGatedRoute.css';

export default function BillingGatedRoute({ children, fallback = '/prep' }) {
  const { status, error, refresh } = useBilling();
  const [retrying, setRetrying] = useState(false);

  if (status === 'loading') {
    return (
      <div className="billing-gate-loading" aria-busy="true">
        <Spinner size="md" />
      </div>
    );
  }

  if (status === 'error') {
    const handleRetry = async () => {
      setRetrying(true);
      try {
        await refresh();
      } finally {
        setRetrying(false);
      }
    };
    return (
      <div className="billing-gate-error" role="alert">
        <p className="billing-gate-error__message">
          {error || "We couldn't check your plan just now."}
        </p>
        {/* `loading` with no `disabled` beside it: the button keeps the focus
            of the press while the retry runs, so a retry that fails too leaves
            the student on "Try again" instead of on <body>. */}
        <Button variant="primary" onClick={handleRetry} loading={retrying}>
          {retrying ? 'Checking…' : 'Try again'}
        </Button>
      </div>
    );
  }

  if (status === 'disabled') {
    return <Navigate to={fallback} replace />;
  }

  return children;
}
